/**
 * ReelHeroMockup · iter r99n · User M1528
 *
 * Route: /promo/reel-hero-mockup
 * Canvas: 1080x1920 (Instagram Reel · 9:16)
 *
 * Direzione User M1528: «Il telefono deve essere reale. Fallo grande, quasi
 * tutto lo schermo. Lo schermo non deve andare fuori il telefono. Il
 * microfono in alto deve essere un'ellisse. Animazioni fluide».
 *
 * Strategia (iter r99n):
 *  1. Foto reale generata da GPT-Image-1: `hand-holding-iphone-big.png`
 *     (1024x1536, iPhone occupa ~85% verticale, mano reale visibile).
 *  2. Coordinate schermo MISURATE pixel-perfect nell'immagine originale:
 *     x:[258→764] y:[267→1222] su canvas 1024x1536.
 *  3. Immagine renderizzata con `object-fit: cover` sul canvas 1080×1920 →
 *     scale factor 1.25, offset X = -100px (cropping laterale).
 *  4. Overlay animazione con MASK RETTANGOLARE dentro `overflow:hidden` +
 *     `border-radius: 55px` per angoli arrotondati come il vero schermo.
 *  5. Dynamic Island MIA (ellisse CSS pulita) sopra l'animazione: copre il
 *     notch dell'immagine AI (che a volte non è perfettamente ellittico).
 */
import React from "react";
import SopralluogoStoryPreview from "@/pages/landing/v2/SopralluogoStoryPreview";

const CANVAS_W = 1080;
const CANVAS_H = 1920;
// iter r99p · Playwright cattura area variabile (~900-980px). Uso 450 come
// centro effettivo del capture — misurato empiricamente.
const CAPTURE_CENTER_X = 450;

// Immagine sorgente 1024x1536. Con object-fit: cover su 1080x1920:
// scale = 1920/1536 = 1.25 → immagine renderizzata 1280x1920.
// Offset X tale che il centro dell'immagine cada in CAPTURE_CENTER_X.
const IMG_W_SRC = 1024;
const IMG_H_SRC = 1536;
const IMG_SCALE = CANVAS_H / IMG_H_SRC; // 1.25
const IMG_W_RENDER = IMG_W_SRC * IMG_SCALE; // 1280
const IMG_OFFSET_X = CAPTURE_CENTER_X - IMG_W_RENDER / 2; // 490 - 640 = -150

// Coordinate schermo (pixel-perfect nell'immagine sorgente).
// iter r99n-fix · misura corretta bordi arrotondati: y_top=155 (non 267,
// che era il primo pixel NON coperto dal notch), y_bottom=1265 (bordo reale
// dello schermo, non il primo "gap" bianco).
const SCREEN_SRC = { left: 255, right: 765, top: 155, bottom: 1265 };
const SCREEN_X = SCREEN_SRC.left * IMG_SCALE + IMG_OFFSET_X;
const SCREEN_Y = SCREEN_SRC.top * IMG_SCALE;
const SCREEN_W = (SCREEN_SRC.right - SCREEN_SRC.left) * IMG_SCALE; // 632.5
const SCREEN_H = (SCREEN_SRC.bottom - SCREEN_SRC.top) * IMG_SCALE; // 1193.75

// Corner radius dello schermo iPhone (misurato ~45px su image → 56 su canvas).
const SCREEN_RADIUS = 55;

// Dynamic Island (Notch) — misure pixel-perfect nell'immagine AI:
// x[429→594] y[170→220] in source (1024x1536) → canvas × 1.25.
const ISLAND_W = Math.round(166 * IMG_SCALE); // 207.5
const ISLAND_H = Math.round(51 * IMG_SCALE); // 64
const ISLAND_TOP = Math.round((170 - SCREEN_SRC.top) * IMG_SCALE); // relativo a SCREEN_Y

// Base dimensions per SopralluogoStoryPreview (progettato mobile ~340×640).
const BASE_W = 340;
// iter r99q · Scale UNIFORME sul WIDTH (no distorsione). Il wrapper ha
// altezza sufficiente a coprire l'intera area sotto il notch. Le scene
// espandono il contenuto via CSS injection (justify-content: space-around).
const SCALE = SCREEN_W / BASE_W;
// Area animazione: sotto il notch, dentro lo schermo.
const ANIM_TOP = ISLAND_TOP + ISLAND_H + 12;
const ANIM_AREA_H = SCREEN_H - ANIM_TOP;

export default function ReelHeroMockup() {
  return (
    <>
      {/* Reset globale + espansione scene per riempire lo schermo. */}
      <style>{`
        html, body, #root { margin: 0; padding: 0; overflow: hidden; background: #000; }
        html { scrollbar-width: none; }
        html::-webkit-scrollbar { display: none; }
        body::-webkit-scrollbar { display: none; }

        /* Espansione contenuti scene per riempire schermo del telefono */
        [data-testid="sst-scene-record"] {
          justify-content: space-between !important;
          padding-bottom: 30px !important;
        }
        [data-testid="sst-scene-record"] > div:last-child {
          display: flex !important;
          flex-direction: column !important;
          justify-content: space-around !important;
          padding-bottom: 20px !important;
        }
        [data-testid="sst-scene-record"] > div:last-child > div {
          padding: 12px 14px !important;
          font-size: 12px !important;
          margin: 4px 0 !important;
        }

        [data-testid="sst-scene-photo"] {
          justify-content: space-between !important;
          padding-bottom: 30px !important;
        }
        [data-testid="sst-scene-photo"] .grid {
          flex: 0 0 auto !important;
        }

        [data-testid="sst-scene-servizi"] .grid {
          flex: 1 !important;
          align-content: space-around !important;
        }

        [data-testid="sst-scene-vetrina"] {
          justify-content: flex-start !important;
          padding-bottom: 30px !important;
        }
        [data-testid="sst-scene-vetrina"] > div:last-child {
          margin-top: auto !important;
        }

        [data-testid="sst-scene-social"] {
          justify-content: space-between !important;
          padding-bottom: 30px !important;
        }
      `}</style>
      <div
        className="fixed inset-0 overflow-hidden"
        style={{ width: CANVAS_W, height: CANVAS_H, background: "#000" }}
        data-testid="reel-hero-mockup"
      >
        {/* Immagine reale: mano che tiene iPhone (background full canvas) */}
        <img
          src="/promo/hand-holding-iphone-big.png"
          alt=""
          className="absolute"
          style={{
            left: IMG_OFFSET_X,
            top: 0,
            width: IMG_W_RENDER,
            height: CANVAS_H,
            objectFit: "cover",
          }}
          draggable={false}
        />

        {/* Schermo: overflow hidden + border-radius = animazione clippata */}
        <div
          className="absolute"
          style={{
            left: SCREEN_X,
            top: SCREEN_Y,
            width: SCREEN_W,
            height: SCREEN_H,
            borderRadius: SCREEN_RADIUS,
            overflow: "hidden",
            background: "#FFFFFF",
          }}
        >
          {/* Wrapper animazione: scale UNIFORME, altezza sufficiente a
              coprire l'intera area sotto il notch. Le scene si espandono
              via CSS injection per riempire tutta l'altezza. */}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: ANIM_TOP,
              width: BASE_W,
              height: ANIM_AREA_H / SCALE,
              transformOrigin: "top left",
              transform: `scale(${SCALE})`,
            }}
          >
            <SopralluogoStoryPreview />
          </div>

          {/* Dynamic Island (notch) sopra l'animazione — dimensioni e posizione
              pixel-perfect estratte dall'immagine AI. */}
          <div
            className="absolute pointer-events-none"
            style={{
              left: (SCREEN_W - ISLAND_W) / 2,
              top: ISLAND_TOP,
              width: ISLAND_W,
              height: ISLAND_H,
              borderRadius: ISLAND_H / 2,
              background: "#000000",
              zIndex: 50,
            }}
          />
        </div>
      </div>
    </>
  );
}
